import { Identifier, TournamentStatus } from '@okletsplay/core';
import { DialogName } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { modalsReducers } from 'app/store/modal/slice';
import { dispatchUnwrap } from 'app/utils/store';
import { useTournamentDetails } from 'modules/tournaments/hooks';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import {
  getTournamentById,
  withdrawTournament,
} from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationDialog } from 'shared/components/ConfirmationDialog';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { withTournamentStatus } from 'shared/HOC/withTournamentStatus';

interface MyTeamWithdrawButtonProps {
  tournamentId?: Identifier;
}

const MyTeamWithdrawButtonComponent: FC<MyTeamWithdrawButtonProps> = ({ tournamentId }) => {
  const dispatch = useDispatch();
  const { isLiveTournament } = useTournamentDetails();
  const myTeam = useSelector(currentTournamentSelector.myTeam);

  const withdrawing = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Withdrawing,
  });

  const openConfirmDialog = useCallback(
    () => dispatch(modalsReducers.openModal({ name: DialogName.Confirmation })),
    [dispatch]
  );

  const onClickHandler = useCallback(() => {
    if (!tournamentId) {
      return;
    }

    dispatchUnwrap(withdrawTournament(tournamentId)).then(() =>
      dispatch(getTournamentById(tournamentId))
    );
  }, [dispatch, tournamentId]);

  if (!myTeam || isLiveTournament) {
    return null;
  }

  return (
    <>
      <LoaderButton
        fullWidth
        loading={withdrawing}
        onClick={openConfirmDialog}
        variant="outlined"
        color="error"
      >
        Withdraw
      </LoaderButton>
      <ConfirmationDialog
        title={`Withdraw ${myTeam?.name}`}
        description="Are you sure you want to withdraw the team?"
        onAccept={onClickHandler}
      />
    </>
  );
};

export const MyTeamWithdrawButton = withTournamentStatus(MyTeamWithdrawButtonComponent, [
  TournamentStatus.Pending,
  TournamentStatus.Live,
  TournamentStatus.Checkin,
]);
